import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  Box, Typography, Grid, Card, CardContent, Button, Stack, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  LinearProgress, Alert, IconButton, Tooltip, LinearProgress as LP,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PeopleIcon from '@mui/icons-material/People';
import {
  MY_CEREMONIES_QUERY,
  MY_STAFFING_REQUIREMENTS_QUERY,
  CREATE_STAFFING_REQUIREMENT_MUTATION,
  UPDATE_STAFFING_REQUIREMENT_MUTATION,
  DELETE_STAFFING_REQUIREMENT_MUTATION,
} from '../graphql/queries';
import { useLov } from '../hooks/useLov';

import Paged from '../components/Paged'; // lists beyond 10 are paged
const STATUS_COLOR = { open: 'warning', filled: 'success', cancelled: 'error' };
const EMPTY_FORM   = { roleName: '', requiredCount: 1, notes: '' };

export default function StaffingPage() {
  const [selectedCeremonyId, setSelected] = useState('');
  const [dialog, setDialog] = useState(false);
  const [editReq, setEdit]  = useState(null);
  const [form, setForm]     = useState(EMPTY_FORM);
  const [error, setError]   = useState('');

  const { options: roleOptions } = useLov('staffing_role');

  const { data: ceremonyData } = useQuery(MY_CEREMONIES_QUERY, { fetchPolicy: 'cache-and-network' });
  const { data, loading, refetch } = useQuery(MY_STAFFING_REQUIREMENTS_QUERY, {
    variables: { ceremonyId: selectedCeremonyId },
    skip: !selectedCeremonyId,
    fetchPolicy: 'cache-and-network',
  });

  const ceremonies = ceremonyData?.myCeremonies?.filter(c => c.status !== 'cancelled') || [];
  const requirements = data?.myStaffingRequirements || [];
  const totalRequired  = requirements.reduce((s, r) => s + r.requiredCount, 0);
  const totalConfirmed = requirements.reduce((s, r) => s + r.confirmedCount, 0);
  const pct = totalRequired ? Math.round((totalConfirmed / totalRequired) * 100) : 0;

  const [createReq] = useMutation(CREATE_STAFFING_REQUIREMENT_MUTATION, {
    onCompleted: () => { setDialog(false); setForm(EMPTY_FORM); refetch(); setError(''); },
    onError: e => setError(e.message),
  });
  const [updateReq] = useMutation(UPDATE_STAFFING_REQUIREMENT_MUTATION, {
    onCompleted: () => { setDialog(false); setEdit(null); setForm(EMPTY_FORM); refetch(); setError(''); },
    onError: e => setError(e.message),
  });
  const [deleteReq] = useMutation(DELETE_STAFFING_REQUIREMENT_MUTATION, { onCompleted: () => refetch() });

  const openCreate = () => { setEdit(null); setForm(EMPTY_FORM); setDialog(true); };
  const openEdit   = (r) => { setEdit(r); setForm({ roleName: r.roleName, requiredCount: r.requiredCount, notes: r.notes || '' }); setDialog(true); };

  const handleSave = () => {
    const vars = { ...form, requiredCount: Number(form.requiredCount) || 1, ceremonyId: selectedCeremonyId };
    if (editReq) updateReq({ variables: { id: editReq.id, ...vars } });
    else createReq({ variables: vars });
  };

  const markFilled = (r) => updateReq({ variables: { id: r.id, status: 'filled', confirmedCount: r.requiredCount } });

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight={700}>Guest Experience & Staffing</Typography>
          <Typography variant="body2" color="text.secondary">Define staffing requirements for each ceremony.</Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate} disabled={!selectedCeremonyId}>Add Role</Button>
      </Stack>

      {/* Ceremony selector */}
      <Card sx={{ mb: 3, px: 2, py: 1.5 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>SELECT CEREMONY</Typography>
        <Stack direction="row" flexWrap="wrap" gap={1}>
          {ceremonies.map(c => (
            <Chip key={c.id} label={c.name} onClick={() => setSelected(c.id === selectedCeremonyId ? '' : c.id)} color={selectedCeremonyId === c.id ? 'primary' : 'default'} variant={selectedCeremonyId === c.id ? 'filled' : 'outlined'} icon={<PeopleIcon />} />
          ))}
        </Stack>
      </Card>

      {!selectedCeremonyId && <Alert severity="info">Select a ceremony above to manage staffing requirements.</Alert>}

      {selectedCeremonyId && (
        <>
          {/* Progress */}
          {requirements.length > 0 && (
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" mb={0.5}>
                  <Typography variant="body2">Staff filled</Typography>
                  <Typography variant="body2" fontWeight={700}>{totalConfirmed} / {totalRequired}</Typography>
                </Stack>
                <LinearProgress variant="determinate" value={pct} color={pct === 100 ? 'success' : 'primary'} sx={{ height: 8, borderRadius: 4 }} />
              </CardContent>
            </Card>
          )}

          {loading && <LP sx={{ mb: 2 }} />}
          {requirements.length === 0 && !loading && <Alert severity="info">No staffing roles defined. Click <strong>Add Role</strong> to start.</Alert>}

          <Grid container spacing={2}>
            <Paged items={requirements}>{rows => rows.map(r => (
              <Grid item xs={12} sm={6} md={4} key={r.id}>
                <Card sx={{ border: '1px solid', borderColor: r.status === 'filled' ? 'success.main' : 'divider' }}>
                  <CardContent>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                      <Box>
                        <Typography variant="subtitle2" fontWeight={700}>{(roleOptions.find(o => o.value === r.roleName) || {}).label || r.roleName}</Typography>
                        <Stack direction="row" spacing={0.75} sx={{ mt: 0.75 }}>
                          <Chip label={`${r.confirmedCount}/${r.requiredCount} confirmed`} size="small" color={r.confirmedCount >= r.requiredCount ? 'success' : 'warning'} />
                          <Chip label={r.status} size="small" color={STATUS_COLOR[r.status] || 'default'} />
                        </Stack>
                        {r.notes && <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>{r.notes}</Typography>}
                      </Box>
                      <Stack direction="row">
                        {r.status !== 'filled' && (
                          <Tooltip title="Mark as filled">
                            <Button size="small" onClick={() => markFilled(r)} sx={{ minWidth: 0, px: 1, fontSize: '0.65rem' }}>Fill</Button>
                          </Tooltip>
                        )}
                        <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(r)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => deleteReq({ variables: { id: r.id } })}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}</Paged>
          </Grid>
        </>
      )}

      {/* Dialog */}
      <Dialog open={dialog} onClose={() => setDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{editReq ? 'Edit Staffing Role' : 'Add Staffing Role'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField select label="Role" value={form.roleName} onChange={e => setForm(f => ({ ...f, roleName: e.target.value }))} fullWidth required>
              {roleOptions.map(r => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}
              <MenuItem value="custom">Custom Role (type below)</MenuItem>
            </TextField>
            {form.roleName === 'custom' && (
              <TextField label="Custom Role Name" value={form.customRole || ''} onChange={e => setForm(f => ({ ...f, roleName: e.target.value }))} fullWidth autoFocus />
            )}
            <TextField label="Required Count" type="number" value={form.requiredCount} onChange={e => setForm(f => ({ ...f, requiredCount: e.target.value }))} fullWidth />
            <TextField label="Notes" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} fullWidth multiline rows={2} />
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setDialog(false); setError(''); }}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={!form.roleName || form.roleName === 'custom'}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
